import { Box, Typography } from "@mui/material";
import { ProfileBox } from "./components/ProfileBox";
import { Category } from "./components/Category";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetPostsQuery } from "@/store/api/postApi";
import { useGetUserByUidQuery } from "@/store/api/userApi";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useLocation, useParams } from "react-router-dom";
import { styles } from "@/pages/Profile/stylesProfile";
import { IPost } from "@/store/api/postApiData";
import Post from "../Home/components/Post";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import CustomLoader from "@/components/UI/CustomLoader/CustomLoader";

const LIMIT = 20;
const Profile = () => {
  const { user: authUser } = useAppSelector((state) => state.auth);
  const { uid: routeUid } = useParams();
  const location = useLocation();
  const uidToUse = routeUid || authUser?.uid;
  const myProfile = uidToUse === authUser?.uid;
  const [selectedFilter, setSelectedFilter] = useState<"New" | "Top">("New");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{ parent: string; child?: string }>({
    parent: "",
  });
  const [loadingPosts, setLoadingPosts] = useState<boolean>(false);
  const { postLoading, editingPost, draftLoading } = useSelector(
    (state: RootState) => state.loading
  );

  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const { data: user, refetch: refetchUser } = useGetUserByUidQuery(uidToUse!, {
    skip: !uidToUse,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [localPosts, setLocalPosts] = useState<IPost[]>([]);
  const updatedPosts = useSelector((state: RootState) => state.updatedPosts.posts);

  useEffect(() => {
    setLocalPosts((prev) => prev.map((p) => updatedPosts[p.uid] || p));
  }, [updatedPosts]);

  const params = useMemo(() => {
    let topic = "";
    let subtype = "";

    if (selectedCategory.parent && selectedCategory.parent !== "All") {
      topic = selectedCategory.parent.toUpperCase();

      if (selectedCategory.parent === "Stocks" && selectedCategory.child) {
        if (selectedCategory.child === "technical") subtype = "TECHNICAL";
        if (selectedCategory.child === "fundamental") subtype = "FUNDAMENTAL";
      }
    }

    return {
      page: page,
      limit: LIMIT,
      text: "",
      author: user?.username,
      cashtag: "",
      hashtag: "",
      timeRange: "",
      who: "",
      order: selectedFilter.includes("New") ? "desc" : sortDirection,
      topic,
      subtype,
      sortBy: selectedFilter.includes("Top") ? "likes" : "createdAt",
      filtersKey: selectedFilter,
    };
  }, [page, selectedCategory, selectedFilter, sortDirection, user?.username]);

  const {
    data: posts,
    error: postsError,
    refetch: refetchPosts,
    isFetching,
  } = useGetPostsQuery(params, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    pollingInterval: 0,
    skip: false,
  });
  const isFetchingRef = useRef(isFetching);
  const hasMoreRef = useRef(hasMore);
  const localPostsLengthRef = useRef(localPosts.length);
  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    localPostsLengthRef.current = localPosts.length;
  }, [localPosts.length]);

  const loadNextPage = useCallback(() => {
    if (!isFetchingRef.current && hasMoreRef.current) {
      setPage((prev) => prev + 1);
    }
  }, []);

  const handleDeletePost = (uid: string) => {
    setLocalPosts((prev) => prev.filter((post) => post.uid !== uid));
  };

  useEffect(() => {
    if (uidToUse) {
      refetchUser();
    }
  }, [location.key, refetchUser, uidToUse]);

  useEffect(() => {
    setPage(1);
    setLocalPosts([]);
    setHasMore(true);
    setLoadingPosts(true);
  }, [selectedFilter, selectedCategory, sortDirection]);

  useEffect(() => {
    if (!posts) return;
    const postsArray = posts.data;
    if (page === 1) {
      setLocalPosts(postsArray);
    } else {
      setLocalPosts((prev) => {
        const updated = prev.map((p) => {
          const newer = postsArray.find((np) => np.uid === p.uid);
          return newer ?? p;
        });

        const newPosts = postsArray.filter((p) => !prev.some((ap) => ap.uid === p.uid));

        return [...updated, ...newPosts];
      });
    }

    setHasMore(postsArray.length === LIMIT);
    setLoadingPosts(false);
  }, [posts, page]);

  const hasPosts = (localPosts?.length ?? 0) > 0;

  useEffect(() => {
    // the sentinel only mounts once `!postsError && !loadingPosts && hasPosts`,
    // so the effect must re-run when any of them flips to (re)attach
    if (postsError || loadingPosts || !hasPosts || !loadMoreRef.current) return;

    observer.current = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        hasMoreRef.current &&
        !isFetchingRef.current &&
        localPostsLengthRef.current > 0
      ) {
        loadNextPage();
      }
    });

    observer.current.observe(loadMoreRef.current);

    return () => observer.current?.disconnect();
  }, [loadNextPage, postsError, loadingPosts, hasPosts]);

  const showTopLoader =
    myProfile && (isFetching || postLoading || draftLoading || refreshLoading || loadingPosts);

  return (
    <Box className={"hide-scrollbar"} sx={styles.container}>
      {!user ? (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CustomLoader />
        </Box>
      ) : (
        <Box>
          <ProfileBox
            Edit={myProfile}
            avatarUrl={user.image?.url || ""}
            likesCount={user.likesCount || 0}
            zapCount={user.zapCount || 0}
            earnings={`${user.balance || 0}`}
            avgLikesPerPost={`${user.likesPerPost}`}
            following={user.followingCount}
            followers={user.followersCount}
            badge={user.rankPercentile ? `Top ${user.rankPercentile}%` : undefined}
            username={user.username}
            bio={user.bio}
            memberSince={user.createdAt}
            tradingStyle={user.tradingStyle}
            tradingMentor={user.tradingMentors}
            isFollowing={user.isSubscribed}
            targetUid={user.uid}
            refetchUser={refetchUser}
            isPremium={user.isPremium}
            socialMedia={user.socialMedia}
            totalEarned={user.totalEarned}
            topEarnerRank={user.topEarnerRank}
          />
          <Category
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            onRefreshPosts={() => {
              setRefreshLoading(true);
              refetchPosts().finally(() => setRefreshLoading(false));
              refetchUser();
            }}
            selectedFilters={selectedFilter ? [selectedFilter] : []}
            setSelectedFilters={(arr) => setSelectedFilter(arr[0] as "New" | "Top")}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
          />

          {postsError ? (
            <Box
              sx={{
                padding: "1rem",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                height: "70vh",
              }}
            >
              <Typography color="text.secondary" sx={{ fontSize: "28px" }}>
                {(postsError as any)?.data?.message || "Something went wrong"}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: "24px" }}>
                {(postsError as any)?.status || (postsError as any)?.data?.statusCode || "Error"}
              </Typography>
            </Box>
          ) : loadingPosts ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "48vh",
              }}
            >
              <CustomLoader />
            </Box>
          ) : hasPosts ? (
            <Box sx={styles.postsWrapper}>
              {showTopLoader && (
                <Box
                  sx={{ display: "flex", justifyContent: "center", py: 2, background: "#f7f7f7" }}
                >
                  <CustomLoader size={34} />
                </Box>
              )}
              {localPosts.map((post: IPost) => {
                const isEditingThisPost = editingPost.loading && editingPost.postId === post.uid;

                return (
                  <Post
                    post={post}
                    key={post.uid}
                    onDelete={handleDeletePost}
                    isEditing={isEditingThisPost}
                  />
                );
              })}

              {isFetching && page > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CustomLoader />
                </Box>
              )}

              <div ref={loadMoreRef} style={{ height: 40 }} />

              {!hasMore && localPosts.length > LIMIT && (
                <Box sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
                  No more posts
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={styles.emptyBox}>
              <span>Nothing here yet. Create your first post to get started!</span>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Profile;
